import { funnelTracker } from './funnelTracker';
import type { LifecycleHandoffDestination } from './lifecycleHandoffContract';
import {
    finishJourney,
    getCurrentJourney,
    inferAuthModeFromStorage,
    resolveScenarioIfPossible,
    resumeJourney,
    startJourney,
    updateJourney,
    type FunnelAuthMode,
    type FunnelJourneyOutcome,
    type FunnelScenario,
    type FunnelJourneyState,
} from './funnelJourney';

interface CommonEventFields {
    project_id?: string | null;
    chat_session_id?: string | null;
    entry_surface?: string;
    entry_path?: string;
    template_project_id?: string | null;
    auth_mode_event?: FunnelAuthMode;
    funnel_journey_id?: string;
}

export interface FunnelEventPropsMap {
    journey_started: CommonEventFields & {
        entry_surface: string;
        entry_path?: string;
        scenario_candidate?: FunnelScenario;
    };
    journey_scenario_resolved: CommonEventFields & {
        resolved_scenario: FunnelScenario;
        trigger_event: string;
    };
    journey_finished: CommonEventFields & {
        outcome: FunnelJourneyOutcome;
        reason?: string;
    };
    chat_opened: CommonEventFields & {
        source: string;
    };
    chat_closed: CommonEventFields & {
        reason?: string;
        turn_count?: number;
        had_summary?: boolean;
    };
    chat_message_sent: CommonEventFields & {
        turn: number;
        message_length: number;
    };
    chat_message_received: CommonEventFields & {
        turn: number;
        message_length: number;
        status?: string;
    };
    chat_summary_ready: CommonEventFields & {
        turn_count: number;
        categories_count: number;
        include_keywords_count: number;
        exclude_keywords_count: number;
        interest_length: number;
    };
    chat_set_up_delivery_clicked: CommonEventFields & {
        turn_count: number;
    };
    chat_change_clicked: CommonEventFields & {
        turn_count: number;
    };
    form_opened: CommonEventFields & {
        source: 'chat' | 'template' | 'form' | 'resume' | 'edit' | 'unknown';
        from_chat: boolean;
        template: boolean;
    };
    onboarding_form_redirected: CommonEventFields & {
        reason: string;
        destination: string;
        from_chat?: boolean;
        template?: boolean;
    };
    project_create_started: CommonEventFields & {
        source: 'chat' | 'template' | 'forward' | 'direct' | 'unknown';
    };
    project_create_succeeded: CommonEventFields & {
        source: 'chat' | 'template' | 'forward' | 'direct' | 'unknown';
        project_id: string;
        requires_verification?: boolean;
        activation_resume_required?: boolean;
    };
    project_create_failed: CommonEventFields & {
        source: 'chat' | 'template' | 'forward' | 'direct' | 'unknown';
        error_code?: string;
        error_message?: string;
    };
    delivery_opened: CommonEventFields & {
        user_type: 'anonymous' | 'authenticated';
        processing_state?: 'active' | 'stalled' | 'completed';
    };
    delivery_save_attempted: CommonEventFields & {
        user_type: 'anonymous' | 'authenticated';
        has_errors: boolean;
        error_fields: string[];
    };
    delivery_save_outcome: CommonEventFields & {
        user_type: 'anonymous' | 'authenticated';
        outcome: 'verification_sent' | 'existing_account_handoff' | 'activated' | 'failed';
        processing_state: 'active' | 'stalled' | 'completed';
    };
    auth_callback_received: CommonEventFields & {
        has_callback_params: boolean;
        auth_type?: string;
        flow?: 'deferred_link' | 'preview_handoff' | 'lifecycle_handoff' | 'lifecycle_recovery' | 'error' | 'unknown';
    };
    auth_handoff_result: CommonEventFields & {
        flow: 'deferred_link' | 'preview_handoff' | 'lifecycle_recovery' | 'otp_code';
        type: string;
        destination: string;
        needs_profile_completion: boolean;
        has_funnel_context: boolean;
    };
    lifecycle_handoff_result: CommonEventFields & {
        event_id: string;
        contact_kind: string;
        destination: LifecycleHandoffDestination;
        outcome: 'authenticated';
    };
    collector_flush_failed: CommonEventFields & {
        reason: string;
        dropped_events?: number;
    };
}

export type FunnelEventName = keyof FunnelEventPropsMap;
export type FunnelEventProps<TEventName extends FunnelEventName> = FunnelEventPropsMap[TEventName];

interface FunnelEnvelope {
    schema_version: '2';
    journey_id: string;
    journey_seq: number;
    auth_mode_start: FunnelAuthMode;
    auth_mode_event: FunnelAuthMode;
    scenario_candidate: FunnelScenario;
    scenario_final: FunnelScenario | null;
    entry_surface: string;
    entry_path: string;
    chat_session_id?: string | null;
    project_id?: string | null;
}

const PRIVACY_BLOCKED_KEYS = new Set([
    'message',
    'messages',
    'content',
    'assistant_message',
    'interest_text',
]);

function sanitizeProperties(properties: Record<string, unknown>): Record<string, unknown> {
    const sanitized: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(properties)) {
        if (typeof value === 'undefined') {
            continue;
        }

        if (PRIVACY_BLOCKED_KEYS.has(key)) {
            continue;
        }

        sanitized[key] = value;
    }

    return sanitized;
}

function buildEnvelope(
    authModeEvent: FunnelAuthMode,
    overrides: Partial<Pick<FunnelEnvelope, 'entry_surface' | 'entry_path' | 'chat_session_id' | 'project_id'>> = {}
): FunnelEnvelope {
    const journey = getCurrentJourney() || startJourney({
        entrySurface: overrides.entry_surface || 'unknown',
        entryPath: overrides.entry_path,
        authModeStart: authModeEvent,
    });

    return {
        schema_version: '2',
        journey_id: journey.journeyId,
        journey_seq: journey.journeySeq,
        auth_mode_start: journey.authModeStart,
        auth_mode_event: authModeEvent,
        scenario_candidate: journey.scenarioCandidate,
        scenario_final: journey.scenarioFinal,
        entry_surface: overrides.entry_surface || journey.entrySurface,
        entry_path: overrides.entry_path || journey.entryPath,
        chat_session_id: overrides.chat_session_id ?? journey.chatSessionId,
        project_id: overrides.project_id ?? journey.projectId,
    };
}

function emitTrackedEvent(eventName: FunnelEventName, properties: Record<string, unknown>) {
    funnelTracker.track(eventName, properties);
}

function maybeEmitScenarioResolved(triggerEvent: FunnelEventName, authModeEvent: FunnelAuthMode) {
    const { state, resolvedScenario } = resolveScenarioIfPossible(triggerEvent);

    if (!state || !resolvedScenario) {
        return;
    }

    const envelope = buildEnvelope(authModeEvent, {
        chat_session_id: state.chatSessionId,
        project_id: state.projectId,
    });

    emitTrackedEvent('journey_scenario_resolved', {
        ...envelope,
        resolved_scenario: resolvedScenario,
        trigger_event: triggerEvent,
    });
}

function maybeEmitJourneyFinished(
    outcome: FunnelJourneyOutcome,
    reason: string | undefined,
    authModeEvent: FunnelAuthMode
) {
    const finishedState = finishJourney(outcome);
    if (!finishedState) {
        return;
    }

    const envelope = buildEnvelope(authModeEvent, {
        chat_session_id: finishedState.chatSessionId,
        project_id: finishedState.projectId,
    });

    emitTrackedEvent('journey_finished', {
        ...envelope,
        outcome,
        reason,
    });
}

function applyJourneyUpdates(eventName: FunnelEventName, properties: Record<string, unknown>, authModeEvent: FunnelAuthMode) {
    const patch: Partial<FunnelJourneyState> = {
        authModeCurrent: authModeEvent,
    };

    if (typeof properties.entry_surface === 'string') {
        patch.entrySurface = properties.entry_surface;
    }

    if (typeof properties.entry_path === 'string') {
        patch.entryPath = properties.entry_path;
    }

    if (typeof properties.chat_session_id === 'string') {
        patch.chatSessionId = properties.chat_session_id;
    }

    if (typeof properties.project_id === 'string') {
        patch.projectId = properties.project_id;
    }

    if (typeof properties.template_project_id === 'string') {
        patch.templateProjectId = properties.template_project_id;
    }

    if (eventName === 'chat_closed') {
        patch.chatClosedBeforeHandoff = true;
    }

    if (eventName === 'form_opened') {
        patch.formSeen = true;
    }

    if (eventName === 'delivery_opened') {
        patch.deliverySeen = true;
    }

    updateJourney(patch);
}

function ensureJourney(eventName: FunnelEventName, properties: Record<string, unknown>, authModeEvent: FunnelAuthMode): boolean {
    const explicitJourneyId = typeof properties.funnel_journey_id === 'string'
        ? properties.funnel_journey_id
        : undefined;
    const current = getCurrentJourney();

    if (explicitJourneyId) {
        const shouldCountAsNew = !current || Boolean(current.finishedAt) || current.journeyId !== explicitJourneyId;
        resumeJourney(explicitJourneyId, {
            entrySurface: typeof properties.entry_surface === 'string' ? properties.entry_surface : undefined,
            entryPath: typeof properties.entry_path === 'string' ? properties.entry_path : undefined,
            templateProjectId: typeof properties.template_project_id === 'string' ? properties.template_project_id : null,
            authModeStart: authModeEvent,
            scenarioCandidate: typeof properties.template_project_id === 'string' ? 'template_to_form' : undefined,
        });
        return shouldCountAsNew;
    }

    if (eventName === 'journey_started') {
        startJourney({
            entrySurface: typeof properties.entry_surface === 'string' ? properties.entry_surface : 'unknown',
            entryPath: typeof properties.entry_path === 'string' ? properties.entry_path : undefined,
            templateProjectId: typeof properties.template_project_id === 'string' ? properties.template_project_id : null,
            authModeStart: authModeEvent,
            chatSessionId: typeof properties.chat_session_id === 'string' ? properties.chat_session_id : null,
            scenarioCandidate: typeof properties.scenario_candidate === 'string'
                ? properties.scenario_candidate as FunnelScenario
                : undefined,
            forceNew: true,
        });
        return true;
    }

    if (!current || current.finishedAt) {
        const templateProjectId = typeof properties.template_project_id === 'string' ? properties.template_project_id : null;
        startJourney({
            entrySurface: typeof properties.entry_surface === 'string' ? properties.entry_surface : 'unknown',
            entryPath: typeof properties.entry_path === 'string' ? properties.entry_path : undefined,
            templateProjectId,
            authModeStart: authModeEvent,
            chatSessionId: typeof properties.chat_session_id === 'string' ? properties.chat_session_id : null,
            scenarioCandidate: templateProjectId ? 'template_to_form' : undefined,
        });
        return true;
    }

    return false;
}

function resolveAutoJourneyCompletion(eventName: FunnelEventName, properties: Record<string, unknown>): {
    outcome: FunnelJourneyOutcome;
    reason?: string;
} | null {
    if (eventName === 'chat_closed') {
        return {
            outcome: 'abandoned',
            reason: typeof properties.reason === 'string' ? properties.reason : 'chat_closed',
        };
    }

    if (eventName === 'onboarding_form_redirected') {
        return {
            outcome: 'redirected',
            reason: typeof properties.reason === 'string' ? properties.reason : 'onboarding_form_redirected',
        };
    }

    if (eventName === 'delivery_save_outcome' && properties.outcome === 'activated') {
        return {
            outcome: 'completed',
            reason: 'delivery_activated',
        };
    }

    if (eventName === 'auth_handoff_result' && properties.needs_profile_completion === false) {
        return {
            outcome: 'completed',
            reason: 'auth_handoff_completed',
        };
    }

    if (eventName === 'lifecycle_handoff_result' && properties.outcome === 'authenticated') {
        return {
            outcome: 'completed',
            reason: 'lifecycle_handoff_completed',
        };
    }

    return null;
}

export function trackFunnelEvent<TEventName extends FunnelEventName>(
    eventName: TEventName,
    properties: FunnelEventProps<TEventName>
) {
    const rawProperties = sanitizeProperties(properties as Record<string, unknown>);
    const propertiesWithoutControl = { ...rawProperties };
    delete propertiesWithoutControl.funnel_journey_id;
    const authModeEvent = (rawProperties.auth_mode_event as FunnelAuthMode | undefined)
        || inferAuthModeFromStorage();

    const createdJourney = ensureJourney(eventName, rawProperties, authModeEvent);

    applyJourneyUpdates(eventName, rawProperties, authModeEvent);

    const currentJourney = getCurrentJourney();
    if (!currentJourney) {
        return;
    }

    const envelope = buildEnvelope(authModeEvent, {
        entry_surface: typeof rawProperties.entry_surface === 'string' ? rawProperties.entry_surface : undefined,
        entry_path: typeof rawProperties.entry_path === 'string' ? rawProperties.entry_path : undefined,
        chat_session_id: typeof rawProperties.chat_session_id === 'string' ? rawProperties.chat_session_id : undefined,
        project_id: typeof rawProperties.project_id === 'string' ? rawProperties.project_id : undefined,
    });

    const payload = {
        ...envelope,
        ...propertiesWithoutControl,
    };

    if (createdJourney && eventName !== 'journey_started') {
        emitTrackedEvent('journey_started', {
            ...envelope,
            entry_surface: envelope.entry_surface,
            entry_path: envelope.entry_path,
            ...(typeof rawProperties.template_project_id === 'string'
                ? { template_project_id: rawProperties.template_project_id }
                : {}),
        });
    }

    maybeEmitScenarioResolved(eventName, authModeEvent);
    emitTrackedEvent(eventName, payload);

    const autoCompletion = resolveAutoJourneyCompletion(eventName, propertiesWithoutControl);
    if (autoCompletion) {
        maybeEmitJourneyFinished(autoCompletion.outcome, autoCompletion.reason, authModeEvent);
    }
}
